// Zustand der Shell-Arbeitsflaeche (docs/shell.md).
//
// Die Shell ist die Huelle der ganzen App: JEDER Inhalt ist ein Tab --
// eine Seite genauso wie ein einzelner Container. Es gibt daher kein
// "offen/zu" mehr wie bei der alten Werkbank (die ein Overlay ueber der
// App war); die Flaeche ist immer da, hoechstens leer.
//
// Ein Tab ist Typ + Identitaet, mehr weiss die Flaeche nicht ueber ihn.
// Was ein Typ bedeutet, steht in der Registry (registry.ts), welche es
// gibt im Manifest (panels.ts). Damit kennt die Huelle keine Domaene.
//
// Zwei Regeln sind aus der Werkbank uebernommen, weil sie sich im
// Betrieb bewaehrt haben:
//   1. Ein Ding ist genau EINMAL offen. Gleicher Typ + gleiche Params
//      = derselbe Tab; wer ihn nochmal oeffnet, springt hin. Sonst
//      laufen zwei Ansichten auf denselben Container auseinander.
//   2. Beim Schliessen uebernimmt der linke Nachbar -- wie in Editoren
//      ueblich, damit der Blick nicht springt.
//
// Regel 2 gilt seit 03.10.2026 nur noch als Rueckfall: zuerst uebernimmt
// der Tab, aus dem der geschlossene geoeffnet wurde (`von`).
//
// Die Leiste ist nach Bereich geordnet (tab-ordnung.ts): ein neuer Tab
// landet im Block seines Bereichs, direkt neben seinem Oeffner, wenn der
// zum selben Bereich gehoert. Ein von Hand verschobener Tab (`platziert`)
// bleibt liegen.
//
// Neu gegenueber der Werkbank: Fokus-Modell (welche Gruppe hoert auf
// die Tastatur), Persistenz ueber prefs (der Stapel ueberlebt den
// Reload) und die Kopplung an die URL (siehe route-sync.svelte.ts).

import { getGeraetPref, prefsGeladen, setGeraetPref } from "../prefs.svelte";
import { findRedirect, tabWurzelFuer } from "../routes";
import { bereichVon, getPanel } from "./registry";
import { einfuegeStelle, istGeordnet, nachBereich, nachfolger, rangVon } from "./tab-ordnung";

export interface ShellTab {
  id: string;
  typ: string;
  params: Record<string, unknown>;
  /** Vorschau-Tab (VS-Code-Sprech: preview). Ein Einfachklick im Baum
   *  oeffnet so: der Tab zeigt den Inhalt, belegt aber keinen festen
   *  Platz -- der naechste Vorschau-Klick ersetzt ihn. Anheften macht
   *  ihn dauerhaft (Doppelklick, Ziehen, oder Arbeiten darin).
   *
   *  Der Sinn: durch einen Baum klicken, ohne 20 Tabs zu erzeugen. */
  temporaer?: boolean;
  /** Von Hand an diese Stelle gezogen: bleibt liegen und zaehlt nicht mehr
   *  zum Block seines Bereichs (tab-ordnung.ts). */
  platziert?: boolean;
  /** Der Tab, aus dem dieser geoeffnet wurde. Beim Schliessen geht es
   *  dorthin zurueck. Nur fuer diese Sitzung -- nach einem Neuladen
   *  uebernimmt der linke Nachbar. */
  von?: string;
  /** Schluessel fuer den Aufbau des Inhalts, wenn er NICHT mehr die ID
   *  ist: gesetzt, sobald die Seite selbst ihren Pfad wechselt
   *  (setzePfadDerSeite). Die ID folgt dem Pfad, der Inhalt soll dabei
   *  stehen bleiben. Nur fuer diese Sitzung. */
  schluessel?: string;
}

export interface TabGroup {
  tabs: ShellTab[];
  activeId: string | null;
}

/** Der Dashboard-Tab. Entspricht der Startseite in VS Code: immer
 *  erreichbar (Aktivitaetsleiste, Strg+Shift+D), aber ein Tab wie
 *  jeder andere -- schliessbar inklusive. */
export const START_TAB = { typ: "page", params: { path: "/" } } as const;

const PREF_KEY = "shell_layout";

/** stableKey macht aus der Identitaet einen vergleichbaren String.
 *  SORTIERT, weil {a,b} und {b,a} dasselbe Ding beschreiben. */
function stableKey(params: Record<string, unknown>): string {
  return Object.keys(params)
    .sort()
    .map((k) => `${k}=${String(params[k])}`)
    .join("&");
}

export function tabId(typ: string, params: Record<string, unknown>): string {
  return `${typ}|${stableKey(params)}`;
}

/** normalisiereParams raeumt einen Seiten-Pfad auf, bevor er zur
 *  Tab-Identitaet wird.
 *
 *  Der Anlass (16.08.): die Meldungen im unteren Panel trugen ihr Ziel
 *  als Hash-Link ("#/system/tasks"), weil sie frueher nur in einem
 *  <a href> landeten. Seit die Meldung einen TAB oeffnet, wurde daraus
 *  ein Tab mit dem Pfad "#/system/tasks" -- und der ist keine Route,
 *  also 404. Die Quelle ist repariert (alerts-store liefert jetzt reine
 *  Pfade wie der Rest der App), aber die Huelle nimmt das Aufraeumen
 *  hier zusaetzlich in die Hand: ein einzelner Aufrufer mit einem '#'
 *  darf keine tote Seite erzeugen koennen, und ein bereits gemerkter
 *  kaputter Tab heilt beim naechsten Wiederherstellen von selbst. */
function normalisiereParams(
  typ: string,
  params: Record<string, unknown>,
): Record<string, unknown> {
  if (typ !== "page") return params;
  const roh = params.path;
  if (typeof roh !== "string") return params;

  let pfad = roh.replace(/^#+/, "");
  if (!pfad.startsWith("/")) pfad = "/" + pfad;
  // Der Query-Teil gehoert in die Adresszeile, nicht in die
  // Tab-Identitaet (20.08.): "/system/tasks?focus=x" ist derselbe TAB
  // wie "/system/tasks", nur mit einem hervorgehobenen Eintrag darauf.
  // Mit dem Query am Pfad findet findRoute() nichts -> 404-Tab. Die
  // Quelle ist repariert (oeffnePfad trennt beides), aber wie beim
  // '#'-Fall raeumt die Huelle zusaetzlich auf: ein gemerkter kaputter
  // Tab heilt so beim naechsten Wiederherstellen von selbst.
  const q = pfad.indexOf("?");
  if (q >= 0) pfad = pfad.slice(0, q);
  // Umgezogene Seite (18.10.2026: /zuhause/drucker -> /werkstatt/drucker):
  // ein gemerkter Tab auf die alte Adresse stünde sonst als „404" da. Die
  // Weiterleitung, die für Lesezeichen gilt, gilt auch für ihn.
  pfad = findRedirect(pfad) ?? pfad;

  return pfad === roh ? params : { ...params, path: pfad };
}

// ─── Zustand ────────────────────────────────────────────────────────

const groups = $state<TabGroup[]>([
  { tabs: [], activeId: null },
  { tabs: [], activeId: null },
]);
let geteilt = $state(false);
let fokus = $state(0);
let splitPct = $state(56);

export function shellGroups(): TabGroup[] {
  return groups;
}
export function shellGeteilt(): boolean {
  return geteilt;
}
export function shellFokus(): number {
  return fokus;
}
export function shellSplitPct(): number {
  return splitPct;
}
export function setShellSplitPct(pct: number): void {
  // Grenzen, damit keine Spalte auf null schrumpft und unerreichbar wird.
  splitPct = Math.min(80, Math.max(20, pct));
  merkeLayout();
}

/** Der Tab, der gerade den Fokus hat. Quelle fuer Breadcrumb, URL und
 *  Tastatur -- bei geteilter Ansicht sind zwei Tabs SICHTBAR, aber nur
 *  einer ist der aktive. */
export function aktiverTab(): ShellTab | null {
  const g = groups[fokus] ?? groups[0];
  return g.tabs.find((t) => t.id === g.activeId) ?? null;
}

/** Rang eines Tabs in der Bereichs-Ordnung (0 Startseite, 1-5 Bereiche,
 *  6 ohne Bereich). */
function rang(t: Pick<ShellTab, "typ" | "params">): number {
  const istStart = t.typ === "page" && String(t.params.path ?? "") === "/";
  return rangVon(bereichVon(t.typ, t.params), istStart);
}
const ordTabs = (g: TabGroup) => g.tabs.map((t) => ({ id: t.id, rang: rang(t), platziert: t.platziert }));

/** Ein Tab wechselt seine ID, wenn er im selben Tab den Pfad tauscht
 *  (Register, Pfadzeile). Wer sich als „geoeffnet aus" auf ihn bezieht,
 *  muss mitziehen -- sonst fuehrt das Schliessen ins Leere. */
function benenneUm(alt: string, neu: string): void {
  if (alt === neu) return;
  for (const g of groups) for (const t of g.tabs) if (t.von === alt) t.von = neu;
}

/** Legt einen Tab nach der Bereichs-Ordnung in eine Spalte. */
function legeEin(g: TabGroup, tab: ShellTab, oeffnerId: string | null): void {
  const { stelle, platziert } = einfuegeStelle(ordTabs(g), rang(tab), oeffnerId);
  tab.platziert = platziert || undefined;
  g.tabs.splice(stelle, 0, tab);
}

export function sichtbarerTab(gruppe: number): ShellTab | null {
  const g = groups[gruppe];
  if (!g) return null;
  return g.tabs.find((t) => t.id === g.activeId) ?? null;
}

export function istOffen(typ: string, params: Record<string, unknown>): boolean {
  const id = tabId(typ, params);
  return groups.some((g) => g.tabs.some((t) => t.id === id));
}

/** istSeiteOffen fragt nach der SEITE, nicht nach dem genauen Pfad.
 *
 *  Wer /infra/storage im Baum markiert sehen will, meint auch den Fall,
 *  dass gerade das Register /infra/network/verlauf offen ist -- es ist
 *  derselbe Tab und dieselbe Seite (Tim 15.08.: "im Tree ist Storage
 *  nicht mehr markiert"). Der exakte Vergleich uebersieht das. */
export function istSeiteOffen(pfad: string): boolean {
  const wurzel = tabWurzelFuer(pfad);
  return groups.some((g) =>
    g.tabs.some(
      (t) => t.typ === "page" && tabWurzelFuer(String(t.params.path ?? "")) === wurzel,
    ),
  );
}

/** istSeiteAktiv: ist diese Seite der Tab mit FOKUS? Derselbe
 *  Wurzel-Vergleich wie istSeiteOffen, damit ein offenes Register zu
 *  seiner Seite zaehlt.
 *
 *  Aktiv ist nur der Tab, den auch Pfadzeile und Adresse zeigen
 *  (aktiverTab). In der geteilten Ansicht gilt der sichtbare Tab der
 *  anderen Spalte darum als "offen", nicht als "aktiv" -- sonst sagte
 *  der Baum nicht mehr, wohin die Tastatur geht. */
export function istSeiteAktiv(pfad: string): boolean {
  const t = aktiverTab();
  return (
    !!t && t.typ === "page" && tabWurzelFuer(String(t.params.path ?? "")) === tabWurzelFuer(pfad)
  );
}

/** istPfadAktiv: steht der Tab mit Fokus GENAU auf diesem Pfad? Fuer
 *  die Zeilen im Baum, seit Register dort eigene Zeilen haben (02.10.):
 *  mit dem Wurzel-Vergleich galten eine Seite und ALLE ihre Register
 *  zugleich als aktiv -- drei markierte Zeilen fuer einen Standort. */
export function istPfadAktiv(pfad: string): boolean {
  const t = aktiverTab();
  return !!t && t.typ === "page" && String(t.params.path ?? "") === pfad;
}

/** istAktiv: wie istOffen, aber nur fuer den Tab mit Fokus. */
export function istAktiv(typ: string, params: Record<string, unknown>): boolean {
  return aktiverTab()?.id === tabId(typ, params);
}

// ─── Oeffnen, wechseln, schliessen ──────────────────────────────────

export interface OeffneOpts {
  /** Zielgruppe. 1 heisst "in die zweite Spalte" und teilt dabei mit --
   *  wer eine zweite Spalte anspricht, will sie sehen. */
  gruppe?: 0 | 1;
  /** Nicht zum neuen Tab springen (Hintergrund-Tab). */
  imHintergrund?: boolean;
  /** Als Vorschau oeffnen (siehe ShellTab.temporaer). */
  vorschau?: boolean;
}

export function oeffneTab(
  typ: string,
  rohParams: Record<string, unknown> = {},
  opts: OeffneOpts = {},
): void {
  if (!getPanel(typ)) {
    // Laut scheitern: ein unbekannter Typ ist ein Verdrahtungsfehler,
    // und ein leerer Tab waere die schlechtere Rueckmeldung.
    throw new Error(`shell: unbekannter Tab-Typ "${typ}"`);
  }
  const params = normalisiereParams(typ, rohParams);
  const id = tabId(typ, params);

  // Regel 1b: REGISTER bleiben im selben Tab.
  //
  // /infra/network/verlauf ist der Reiter "Verlauf" AUF /infra/network --
  // dieselbe Seite, nicht die naechste. Ohne das bekaeme jeder
  // Reiterwechsel einen eigenen Tab (Tim 15.08.: "eigentlich ist es
  // hier ein Sub-Tab"). Wir tauschen deshalb den Pfad im vorhandenen
  // Tab, statt einen zweiten aufzumachen.
  if (typ === "page") {
    const wurzel = tabWurzelFuer(String(params.path ?? ""));
    for (let i = 0; i < groups.length; i++) {
      const vorhanden = groups[i].tabs.find(
        (tb) => tb.typ === "page" && tabWurzelFuer(String(tb.params.path ?? "")) === wurzel,
      );
      if (vorhanden && vorhanden.id !== id) {
        benenneUm(vorhanden.id, id);
        vorhanden.params = { ...params };
        vorhanden.id = id;
        if (!opts.vorschau) vorhanden.temporaer = false;
        groups[i].activeId = id;
        fokus = i;
        merkeLayout();
        return;
      }
    }
  }

  // Regel 1: schon offen -> dorthin springen, egal in welcher Spalte.
  // Ein angehefteter Tab bleibt dabei angeheftet: eine Vorschau darf
  // nie etwas ZURUECKSTUFEN, was der Nutzer schon festgemacht hat.
  for (let i = 0; i < groups.length; i++) {
    const vorhanden = groups[i].tabs.find((t) => t.id === id);
    if (vorhanden) {
      if (!opts.vorschau) vorhanden.temporaer = false;
      groups[i].activeId = id;
      fokus = i;
      merkeLayout();
      return;
    }
  }

  let ziel = opts.gruppe ?? fokus;
  if (opts.gruppe === 1) geteilt = true;
  if (!geteilt) ziel = 0;

  const g = groups[ziel];
  const neu: ShellTab = { id, typ, params, temporaer: !!opts.vorschau };
  // Der Oeffner: der Tab, auf dem man gerade steht. Dorthin geht es beim
  // Schliessen zurueck, und neben ihm landet der neue Tab, wenn beide zum
  // selben Bereich gehoeren.
  let oeffner = g.activeId && g.activeId !== id ? g.activeId : null;

  // Eine Vorschau ERSETZT die vorherige Vorschau derselben Spalte. Im
  // selben Bereich an genau ihrer Stelle -- sonst waere der Baum wieder
  // eine Tab-Schleuder, und die Leiste wuerde beim Durchklicken vor den
  // Augen wandern. Wechselt der Bereich, zieht sie in dessen Block um.
  const vorschauIdx = opts.vorschau ? g.tabs.findIndex((t) => t.temporaer) : -1;
  if (vorschauIdx >= 0) {
    const alt = g.tabs[vorschauIdx];
    // Stand man auf der alten Vorschau, erbt die neue deren Oeffner: wer
    // durch den Baum klickt und dann schliesst, will dorthin zurueck, wo
    // er vor dem Klicken war -- nicht zu einem Tab, den es nicht mehr gibt.
    if (oeffner === alt.id) oeffner = alt.von && g.tabs.some((t) => t.id === alt.von) ? alt.von : null;
    benenneUm(alt.id, id);
    if (rang(alt) === rang(neu)) {
      neu.platziert = alt.platziert;
      g.tabs[vorschauIdx] = neu;
    } else {
      g.tabs.splice(vorschauIdx, 1);
      legeEin(g, neu, oeffner);
    }
  } else {
    legeEin(g, neu, oeffner);
  }
  if (oeffner) neu.von = oeffner;

  if (!opts.imHintergrund) {
    groups[ziel].activeId = id;
    fokus = ziel;
  } else if (!groups[ziel].activeId) {
    groups[ziel].activeId = id;
  }
  merkeLayout();
}

/** hefteAn macht aus einer Vorschau einen dauerhaften Tab. Ausgeloest
 *  durch Doppelklick auf den Tab oder im Baum -- und durch Ziehen: wer
 *  einen Tab an eine Stelle schiebt, meint ihn. */
// Eine Ebene hoch, OHNE den Tab zu wechseln.
//
// Der Pfad ueber dem Editor soll zurueckfuehren -- er tat es, aber er
// sprang dabei in einen anderen Tab: "System" oeffnete die System-Seite
// als eigenen Tab (oder holte einen vorhandenen nach vorn), und der
// Tab, in dem man gerade arbeitete, blieb zurueck. Damit war der Weg
// zurueck ein Ortswechsel und keine Bewegung (Tim 18.08.).
//
// Jetzt tauscht der Klick den Pfad IM aktiven Tab -- dieselbe Bewegung,
// die Register schon machen (Regel 1b). Ist das Ziel woanders schon
// offen, gewinnt weiterhin "eine Sache ist einmal offen": dann springen
// wir dorthin, statt sie ein zweites Mal aufzumachen.
export function gehePfadImTab(pfad: string): boolean {
  const params = normalisiereParams("page", { path: pfad });
  const id = tabId("page", params);

  for (const g of groups) {
    if (g.tabs.some((t) => t.id === id)) return false; // schon offen
  }
  const g = groups[fokus];
  const aktiv = g?.tabs.find((t) => t.id === g.activeId);
  if (!aktiv || aktiv.typ !== "page") return false;

  benenneUm(aktiv.id, id);
  aktiv.params = { ...params };
  aktiv.id = id;
  // Ein bewusster Schritt heftet an: eine Vorschau, die man weiterbenutzt,
  // ist keine Vorschau mehr.
  aktiv.temporaer = false;
  g.activeId = id;
  merkeLayout();
  return true;
}

/** Die Seite selbst meldet, welches ihrer Dinge gerade offen ist.
 *
 *  Der Baum fuehrt zur Seite: ein Klick auf „Homeserver2" setzt den Pfad
 *  und die Cluster-Seite klappt die Zeile auf. Das hier ist die
 *  Gegenrichtung (Tim 04.10.2026: „Wird ein Node erweitert, sollte im
 *  Baum auch das Element markiert sein"): klappt man die Zeile auf der
 *  Seite auf, wechselt der Pfad IHRES Tabs auf /infra/cluster/<rechner>
 *  -- Baum, Pfadzeile und Adresse ziehen von selbst nach, weil sie alle
 *  vom Pfad des Tabs leben.
 *
 *  Anders als gehePfadImTab baut das den Inhalt NICHT neu auf: die Seite
 *  zeigt den Zustand ja schon. Darum behaelt der Tab seinen Aufbau-
 *  Schluessel, waehrend die ID dem Pfad folgt. Und es legt keinen
 *  Verlaufs-Eintrag an (die Adresse wird ersetzt): die Zurueck-Taste
 *  soll nicht durch aufgeklappte Zeilen laufen.
 *
 *  Nur innerhalb derselben Seite (gleiche tabWurzel); `alt` ist der Pfad,
 *  den die Seite gerade hat -- so trifft es auch in der geteilten Ansicht
 *  den richtigen Tab. */
export function setzePfadDerSeite(alt: string, neu: string): boolean {
  if (alt === neu || tabWurzelFuer(alt) !== tabWurzelFuer(neu)) return false;
  const altId = tabId("page", normalisiereParams("page", { path: alt }));
  const params = normalisiereParams("page", { path: neu });
  const id = tabId("page", params);
  for (const g of groups) {
    const tab = g.tabs.find((t) => t.id === altId);
    if (!tab) continue;
    tab.schluessel ??= tab.id;
    benenneUm(tab.id, id);
    if (g.activeId === tab.id) g.activeId = id;
    tab.params = { ...params };
    tab.id = id;
    // Wer in der Seite arbeitet, meint sie: keine Vorschau mehr.
    tab.temporaer = false;
    merkeLayout();
    return true;
  }
  return false;
}

export function hefteAn(gruppe: number, id: string): void {
  const tab = groups[gruppe]?.tabs.find((t) => t.id === id);
  if (!tab?.temporaer) return;
  tab.temporaer = false;
  merkeLayout();
}

export function aktiviere(gruppe: number, id: string): void {
  const g = groups[gruppe];
  if (!g || !g.tabs.some((t) => t.id === id)) return;
  g.activeId = id;
  fokus = gruppe;
  merkeLayout();
}

/** springeZuTab holt den Tab mit dieser ID nach vorn, egal in welcher
 *  Spalte er liegt. false: es gibt ihn nicht (mehr). */
export function springeZuTab(id: string): boolean {
  for (let i = 0; i < groups.length; i++) {
    if (groups[i].tabs.some((t) => t.id === id)) {
      aktiviere(i, id);
      return true;
    }
  }
  return false;
}

/** schliesseTabNachId schliesst den Tab mit dieser ID, egal in welcher
 *  Spalte. Der Fokus geht dabei wie immer an den Oeffner. */
export function schliesseTabNachId(id: string): boolean {
  for (let i = 0; i < groups.length; i++) {
    if (groups[i].tabs.some((t) => t.id === id)) {
      schliesseTab(i, id);
      return true;
    }
  }
  return false;
}

/** zurueckZu: der Weg zurück im eigenen Kopf einer Seite (eigenerPfad:
 *  „Konstruktionen ›", „Claude ›"). Ist das Ziel schon als Tab offen,
 *  geht es dorthin und der Tab DIESER Seite schließt; sonst wird dieser
 *  Tab zum Ziel. So sammeln sich beim Suchen keine Tabs (Tim 20.10.2026:
 *  „Sonst sammelt man die Tabs und muss diese dann wieder schließen").
 *  false: die Seite steht in keinem Tab (Telefon) -- der Aufrufer
 *  navigiert selbst. */
export function zurueckZu(eigenerPfad: string, ziel: string): boolean {
  const eigen = tabId("page", normalisiereParams("page", { path: eigenerPfad }));
  if (!groups.some((g) => g.tabs.some((t) => t.id === eigen))) return false;
  if (springeZuSeite(ziel)) {
    schliesseTabNachId(eigen);
    return true;
  }
  springeZuTab(eigen);
  return gehePfadImTab(ziel);
}

/** springeZuSeite holt den Seiten-Tab zu diesem Pfad nach vorn, wenn er
 *  offen ist -- OHNE ihn anzuheften. Fuer den Schritt zurueck im Verlauf:
 *  wer nur zurueckgeht, hat eine Vorschau nicht "gemeint". */
export function springeZuSeite(pfad: string): boolean {
  return springeZuTab(tabId("page", normalisiereParams("page", { path: pfad })));
}

/** oeffnerVon liefert den Tab, aus dem `tab` geoeffnet wurde -- sofern es
 *  ihn noch gibt. Der Weg zurueck fuer Tabs ohne eigene Adresse (ein
 *  Dokument, ein Container): die Pfadzeile ueber dem Inhalt fuehrt dorthin. */
export function oeffnerVon(tab: ShellTab): ShellTab | null {
  if (!tab.von) return null;
  for (const g of groups) {
    const t = g.tabs.find((x) => x.id === tab.von);
    if (t) return t;
  }
  return null;
}

/** fokussiere setzt nur den Fokus (Klick irgendwo in eine Spalte),
 *  ohne den aktiven Tab dieser Spalte zu wechseln. */
export function fokussiere(gruppe: number): void {
  if (!groups[gruppe] || fokus === gruppe) return;
  fokus = gruppe;
  merkeLayout();
}

export function schliesseTab(gruppe: number, id: string): void {
  const g = groups[gruppe];
  if (!g) return;
  const idx = g.tabs.findIndex((t) => t.id === id);
  if (idx < 0) return;
  const [weg] = g.tabs.splice(idx, 1);

  // Zurueck zu dem Tab, aus dem er geoeffnet wurde; gibt es den nicht
  // mehr, uebernimmt der linke Nachbar (Regel 2).
  if (g.activeId === id) g.activeId = nachfolger(g.tabs, idx, weg.von);
  // Was aus dem geschlossenen Tab geoeffnet wurde, haengt jetzt an dessen
  // eigenem Oeffner -- die Kette bleibt, das Glied faellt weg.
  for (const t of g.tabs) if (t.von === id) t.von = weg.von;

  // Leere rechte Spalte hebt die Teilung auf -- eine leere Haelfte, die
  // Platz frisst, waere nur im Weg.
  if (geteilt && groups[1].tabs.length === 0) {
    geteilt = false;
    fokus = 0;
  }
  if (fokus === 1 && groups[1].tabs.length === 0) fokus = 0;
  merkeLayout();
}

export function schliesseAndere(gruppe: number, id: string): void {
  const g = groups[gruppe];
  if (!g) return;
  g.tabs = g.tabs.filter((t) => t.id === id);
  g.activeId = g.tabs.length ? id : null;
  merkeLayout();
}

/** schliesseRechts raeumt alles rechts vom angegebenen Tab weg -- die
 *  Geste "ab hier ist es Ballast". Der Tab selbst bleibt.
 *
 *  Ueber schliesseTab statt per Filter: nur so greifen die Regeln fuer
 *  den nachrueckenden aktiven Tab und fuer die leer gewordene zweite
 *  Spalte. Die PATCHes buendelt merkeLayout ohnehin zu einem. */
export function schliesseRechts(gruppe: number, id: string): void {
  const g = groups[gruppe];
  if (!g) return;
  const idx = g.tabs.findIndex((t) => t.id === id);
  if (idx < 0) return;
  for (const tab of g.tabs.slice(idx + 1)) schliesseTab(gruppe, tab.id);
}

/** anzahlRechts sagt, ob "Rechts davon schliessen" ueberhaupt etwas
 *  zu tun haette -- das Menue graut den Eintrag sonst aus, statt eine
 *  wirkungslose Schaltflaeche anzubieten. */
export function anzahlRechts(gruppe: number, id: string): number {
  const g = groups[gruppe];
  if (!g) return 0;
  const idx = g.tabs.findIndex((t) => t.id === id);
  return idx < 0 ? 0 : g.tabs.length - idx - 1;
}

/** schliesseAlle leert eine Spalte. */
export function schliesseAlle(gruppe: number): void {
  const g = groups[gruppe];
  if (!g) return;
  for (const tab of [...g.tabs]) schliesseTab(gruppe, tab.id);
}

/** schliesseTabsWo raeumt alle Tabs weg, auf die pred zutrifft.
 *  Mechanik, keine Politik: WELCHE Tabs im Weg sind, weiss nur der
 *  Aufrufer (die Entgelt-Seite raeumt so den vorigen Beleg weg). */
export function schliesseTabsWo(pred: (t: ShellTab) => boolean): void {
  for (let i = 0; i < groups.length; i++) {
    for (const tab of [...groups[i].tabs]) {
      if (pred(tab)) schliesseTab(i, tab.id);
    }
  }
}

// ─── Teilen ─────────────────────────────────────────────────────────

/** teile schiebt den aktiven Tab in die andere Spalte -- die Geste
 *  "ich brauche das hier NEBEN dem anderen". Ist schon geteilt, wird
 *  wieder zusammengelegt. */
export function teile(): void {
  if (!geteilt) {
    const von = groups[0];
    const tab = von.tabs.find((t) => t.id === von.activeId);
    if (!tab) {
      // Nichts zu schieben: Teilung nur vorbereiten.
      geteilt = true;
      fokus = 1;
      merkeLayout();
      return;
    }
    if (von.tabs.length < 2) {
      // Mit nur einem Tab gaebe es links nichts mehr zu sehen -- dann
      // bleibt er links und rechts wird eine Kopie gezeigt. Das ist
      // der Fall "denselben Log zweimal, an verschiedenen Stellen"
      // NICHT: Regel 1 verbietet Doppel. Also nur vorbereiten.
      geteilt = true;
      fokus = 1;
      merkeLayout();
      return;
    }
    von.tabs = von.tabs.filter((t) => t.id !== tab.id);
    von.activeId = von.tabs[von.tabs.length - 1]?.id ?? null;
    tab.von = undefined;
    groups[1].tabs.push(tab);
    groups[1].activeId = tab.id;
    geteilt = true;
    fokus = 1;
    merkeLayout();
    return;
  }
  // Zurueck zu einer Spalte: alles nach links, jeder Tab in den Block
  // seines Bereichs (von Hand Verschobenes ans Ende).
  for (const tab of groups[1].tabs) {
    if (tab.platziert) groups[0].tabs.push(tab);
    else legeEin(groups[0], tab, null);
  }
  if (groups[1].activeId) groups[0].activeId = groups[1].activeId;
  groups[1].tabs = [];
  groups[1].activeId = null;
  geteilt = false;
  fokus = 0;
  merkeLayout();
}

/** verschiebeTab ist DIE Bewegung: innerhalb einer Spalte umsortieren
 *  und zwischen den Spalten wechseln sind derselbe Vorgang, nur mit
 *  anderer Zielspalte. Genau deshalb eine Funktion und nicht zwei --
 *  sonst driften die Sonderfaelle (letzter Tab, aktiver Tab, Teilung
 *  aufheben) auseinander.
 *
 *  zielIndex ist die Stelle in der ZIEL-Liste, an der der Tab landen
 *  soll; undefined heisst ans Ende. Ein Tab, den man bewusst irgendwo
 *  hinlegt, wird dabei angeheftet -- eine Vorschau, die man einsortiert,
 *  ist keine Vorschau mehr. */
export function verschiebeTab(
  vonGruppe: number,
  id: string,
  nachGruppe: number,
  zielIndex?: number,
): void {
  const von = groups[vonGruppe];
  const ziel = groups[nachGruppe];
  if (!von || !ziel) return;
  const idx = von.tabs.findIndex((t) => t.id === id);
  if (idx < 0) return;

  // Zweite Spalte anzusprechen heisst, sie sehen zu wollen.
  if (nachGruppe === 1) geteilt = true;

  const [tab] = von.tabs.splice(idx, 1);
  tab.temporaer = false;
  // In einer anderen Spalte gibt es den Oeffner nicht.
  if (vonGruppe !== nachGruppe) tab.von = undefined;

  if (zielIndex === undefined) {
    // Ohne Zielstelle (Knopf „in die andere Spalte", Ablegen auf der
    // Teilen-Zone): in den Block seines Bereichs.
    legeEin(ziel, tab, null);
  } else {
    // An eine bestimmte Stelle gezogen: dort bleibt er liegen (Tim
    // 03.10.2026: „Wird einer manuell woanders hingeschoben, dann bleibt
    // der dort").
    let stelle = zielIndex;
    // Innerhalb derselben Liste verschiebt das Herausnehmen alles danach
    // um eins nach vorn -- ohne diese Korrektur landet der Tab beim
    // Rechtsziehen immer eine Position zu weit links.
    if (vonGruppe === nachGruppe && zielIndex > idx) stelle = zielIndex - 1;
    stelle = Math.max(0, Math.min(stelle, ziel.tabs.length));
    ziel.tabs.splice(stelle, 0, tab);
    // Liegt er nach dem Ziehen ohnehin in seinem Block, ist er nicht
    // „woanders" -- dann bleibt er Teil der Ordnung.
    const l = ziel.tabs[stelle - 1];
    const r = ziel.tabs[stelle + 1];
    const meiner = rang(tab);
    const imBlock =
      (l && !l.platziert && rang(l) === meiner) ||
      (r && !r.platziert && rang(r) === meiner) ||
      ((!l || rang(l) <= meiner) && (!r || rang(r) >= meiner));
    tab.platziert = imBlock ? undefined : true;
  }

  if (vonGruppe !== nachGruppe && von.activeId === id) {
    const nachbar = von.tabs[idx - 1] ?? von.tabs[idx] ?? null;
    von.activeId = nachbar ? nachbar.id : null;
  }
  ziel.activeId = id;
  fokus = nachGruppe;

  // Leere rechte Spalte hebt die Teilung auf -- wie beim Schliessen.
  if (groups[1].tabs.length === 0) {
    geteilt = false;
    fokus = 0;
  }
  merkeLayout();
}

/** ordneNachBereich stellt die Ordnung einer Spalte wieder her: alle Tabs
 *  nach Bereich, auch die von Hand verschobenen -- wer das waehlt, will
 *  aufraeumen. */
export function ordneNachBereich(gruppe: number): void {
  const g = groups[gruppe];
  if (!g) return;
  const geordnet = nachBereich(g.tabs.map((t) => ({ t, rang: rang(t) }))).map((x) => x.t);
  for (const t of geordnet) t.platziert = undefined;
  g.tabs = geordnet;
  merkeLayout();
}

/** Hat „Nach Bereich ordnen" in dieser Spalte etwas zu tun? */
export function leisteGeordnet(gruppe: number): boolean {
  const g = groups[gruppe];
  return !g || istGeordnet(ordTabs(g));
}

/** Beginnt mit diesem Tab ein neuer Bereich? Fuer die feine Trennung in
 *  der Leiste. */
export function beginntBlock(gruppe: number, id: string): boolean {
  const g = groups[gruppe];
  const index = g ? g.tabs.findIndex((t) => t.id === id) : -1;
  if (!g || index <= 0) return false;
  return rang(g.tabs[index]) !== rang(g.tabs[index - 1]);
}

/** verschiebeInAndereGruppe: der Knopf in der Tab-Leiste. Legt den Tab
 *  ans Ende der Nachbarspalte. */
export function verschiebeInAndereGruppe(gruppe: number, id: string): void {
  verschiebeTab(gruppe, id, gruppe === 0 ? 1 : 0);
}

// ─── Persistenz ─────────────────────────────────────────────────────
//
// Der Stapel gehoert dem Nutzer, nicht dem Tab: ein Reload (oder ein
// Deploy, der die WS kappt) darf die Arbeit nicht wegraeumen. Ablage
// ueber prefs -- damit synchronisiert er nebenbei ueber Geraete, ohne
// dass hier etwas dafuer zu tun waere.

interface GemerktesLayout {
  gruppen: {
    tabs: { typ: string; params: Record<string, unknown>; temporaer?: boolean; platziert?: boolean }[];
    activeId: string | null;
  }[];
  geteilt: boolean;
  fokus: number;
  splitPct: number;
  /** 1 = die Leiste wurde schon einmal nach Bereich geordnet. Fehlt die
   *  Marke (Stapel von vor 03.10.2026), ordnet das Wiederherstellen ihn
   *  einmal; danach gilt die gemerkte Reihenfolge. */
  ordnung?: number;
}

let merkenGeplant = false;
function merkeLayout(): void {
  // Gebuendelt in den naechsten Microtask: ein Klick loest oft mehrere
  // Aenderungen aus (oeffnen + aktivieren + fokussieren), und daraus
  // sollen nicht drei PATCHes werden.
  if (merkenGeplant) return;
  merkenGeplant = true;
  queueMicrotask(() => {
    merkenGeplant = false;
    // ⚠️ NICHTS schreiben, solange der Server-Stand nicht da ist.
    //
    // Der Fall, der das erzwungen hat (21.08.2026): Auf einem Browser
    // OHNE warmen localStorage-Cache -- neues Geraet, geleerte
    // Website-Daten, privates Fenster, jeder Headless-Lauf von
    // scripts/agent-verify.sh -- rendert die Shell, bevor
    // loadPrefsFromServer() zurueck ist. bootLayout() findet dann
    // nichts, oeffnet die Startseite, und dieser Schreiber hat den
    // gemerkten Stapel mit EINEM Tab ueberschrieben. Nicht nur nicht
    // wiederhergestellt: geloescht -- damals auf allen Geraeten zugleich.
    // Seit 10.10.2026 gehoert der Stapel dem Geraet (prefs-geraet.ts);
    // die Sperre bleibt, sie schuetzt jetzt den eigenen.
    //
    // Vor dem Server-Stand ist kein Zustand eine Entscheidung des
    // Nutzers. Was in diesem Fenster passiert, wird von der naechsten
    // echten Aenderung ohnehin mitgeschrieben.
    if (!prefsGeladen()) return;
    const daten: GemerktesLayout = {
      gruppen: groups.map((g) => ({
        tabs: g.tabs.map((t) => ({ typ: t.typ, params: t.params, temporaer: t.temporaer, platziert: t.platziert })),
        activeId: g.activeId,
      })),
      geteilt,
      fokus,
      splitPct,
      ordnung: 1,
    };
    setGeraetPref(PREF_KEY, daten);
  });
}

/** stelleLayoutHer laedt den gemerkten Stapel. Gibt zurueck, ob dabei
 *  etwas herauskam -- ist nichts da, legt der Aufrufer den Start-Tab an.
 *
 *  Unbekannte Typen werden STILL uebersprungen: ein Panel-Typ kann mit
 *  einem Deploy verschwinden, und ein alter Eintrag darf dann nicht den
 *  ganzen Stapel verlieren lassen. */
// Pfade, die beim Wiederherstellen als Vorschau verworfen wurden.
// bootLayout fragt das ab: sonst holt es genau diesen Pfad gleich aus
// der Adresszeile zurueck -- und zwar als vollwertigen Tab.
const verworfeneVorschau = new Set<string>();

export function warVorschau(pfad: string): boolean {
  return verworfeneVorschau.has(pfad);
}

// hergestellt: hat ein Wiederherstellen jemals etwas zurueckgebracht?
// Der zweite Versuch nach dem Server-Sync fragt das ab -- er soll einen
// bereits stehenden Stapel nicht noch einmal umwerfen.
let hergestellt = false;

export function layoutWurdeHergestellt(): boolean {
  return hergestellt;
}

/** unberuehrterStart: sieht es noch genau so aus wie direkt nach dem
 *  Boot (hoechstens ein Tab, nicht geteilt)? Nur dann darf ein spaeter
 *  eintreffender Server-Stand den Stapel noch ersetzen -- wer in der
 *  Zwischenzeit selbst etwas geoeffnet hat, soll es behalten. */
export function unberuehrterStart(): boolean {
  return !geteilt && groups[1].tabs.length === 0 && groups[0].tabs.length <= 1;
}

export function stelleLayoutHer(): boolean {
  const daten = getGeraetPref<GemerktesLayout>(PREF_KEY);
  if (!daten?.gruppen?.length) return false;

  let irgendwas = false;
  // Eine Seite hat EINEN Tab (Regel 1b in oeffneTab). Ein gemerkter Stapel
  // kann das verletzen, wenn eine Route erst später zum Register ihrer
  // Seite wurde -- so am 04.10.2026 der einzelne Rechner: gemerkt waren
  // „Cluster" und „homeserver2" als zwei Tabs. Der erste bleibt, der
  // zweite geht in ihm auf; war der zweite der aktive, wird es der erste.
  const proSeite = new Map<string, string>(); // Wurzel -> Tab-ID, die bleibt
  const aufgegangen = new Map<string, string>(); // verworfene ID -> bleibende ID
  daten.gruppen.slice(0, 2).forEach((gespeichert, i) => {
    const ziel = groups[i];
    if (!ziel) return;
    ziel.tabs = [];
    for (const t of gespeichert.tabs ?? []) {
      if (!t?.typ || !getPanel(t.typ)) continue;
      // Vorschau-Tabs ueberleben den Reload NICHT (Tim 18.08.). Ein
      // Tab in Kursiv ist der Merkzettel fuer "nur mal reingeschaut" --
      // er wird vom naechsten Einfachklick ersetzt und war nie als
      // Bestand gemeint. Ihn wiederherzustellen macht aus einem Blick
      // rueckwirkend eine Entscheidung.
      if (t.temporaer) {
        if (t.typ === "page") verworfeneVorschau.add(String(t.params?.path ?? ""));
        continue;
      }
      const params = normalisiereParams(t.typ, t.params ?? {});
      const id = tabId(t.typ, params);
      if (ziel.tabs.some((x) => x.id === id)) continue;
      if (t.typ === "page") {
        const wurzel = tabWurzelFuer(String(params.path ?? ""));
        const bleibt = proSeite.get(wurzel);
        if (bleibt) {
          aufgegangen.set(id, bleibt);
          continue;
        }
        proSeite.set(wurzel, id);
      }
      ziel.tabs.push({ id, typ: t.typ, params, temporaer: t.temporaer, platziert: t.platziert || undefined });
      irgendwas = true;
    }
    // Stapel von vor der Bereichs-Ordnung: einmal ordnen.
    if (!daten.ordnung) {
      ziel.tabs = nachBereich(ziel.tabs.map((t) => ({ t, rang: rang(t) }))).map((x) => x.t);
    }
    const aktiv = aufgegangen.get(gespeichert.activeId ?? "") ?? gespeichert.activeId;
    ziel.activeId =
      ziel.tabs.find((t) => t.id === aktiv)?.id ??
      ziel.tabs[ziel.tabs.length - 1]?.id ??
      null;
  });

  if (!irgendwas) return false;
  hergestellt = true;
  geteilt = !!daten.geteilt && groups[1].tabs.length > 0;
  fokus = geteilt && daten.fokus === 1 ? 1 : 0;
  if (typeof daten.splitPct === "number") {
    splitPct = Math.min(80, Math.max(20, daten.splitPct));
  }
  return true;
}
